import { z } from "zod";

const searchFormSchema = z
  .object({
    tripType: z.enum(["oneWay", "roundTrip"]),
    departure: z.string().min(1, "Departure is required"),
    destination: z.string().min(1, "Destination is required"),
    passengers: z.object({
      adults: z.number().min(0),
      teenagers: z.number().min(0),
      children: z.number().min(0),
      infants: z.number().min(0),
    }),
    useAdvantageChecked: z.boolean(),
    promoCode: z.string().optional(),
    travelDates: z.object({
      from: z.union([z.date(), z.null(), z.undefined()]),
      to: z.union([z.date(), z.null(), z.undefined()]),
    }),
  })
  .superRefine((data, ctx) => {
    const { adults, teenagers } = data.passengers;
    if ((adults ?? 0) + (teenagers ?? 0) < 1) {
      ctx.addIssue({
        path: ["passengers"],
        code: z.ZodIssueCode.custom,
        message: "At least 1 adult or 1 teenager is required.",
      });
    }

    // Require 'from' on submit
    if (!(data.travelDates.from instanceof Date)) {
      ctx.addIssue({
        path: ["travelDates", "from"],
        code: z.ZodIssueCode.custom,
        message: "Travel date is required.",
      });
    }

    // Require 'to' only if roundTrip
    if (data.tripType === "roundTrip" && !(data.travelDates.to instanceof Date)) {
      ctx.addIssue({
        path: ["travelDates", "to"],
        code: z.ZodIssueCode.custom,
        message: "Return date is required.",
      });
    }
  });

type SearchFormData = z.infer<typeof searchFormSchema>;

const defaultValues: SearchFormData = {
  tripType: "roundTrip",
  departure: "",
  destination: "",
  passengers: { adults: 1, teenagers: 0, children: 0, infants: 0 },
  useAdvantageChecked: false,
  promoCode: "",
  travelDates: {
    from: null, // empty by default
    to: null, // empty by default
  },
};

export { searchFormSchema, defaultValues, type SearchFormData };
